import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { productUrl } from '../utils/slug';
import { getCollectionStatus, ALL_TIERS } from '../utils/collectionStatus';
import { ReferralModal } from '../components/ReferralModal';
import { FeedbackModal } from '../components/FeedbackModal';
import { SEO } from '../components/SEO';
import './StashPage.css';
import { API_BASE } from '../config';

interface UserReview {
  productId: string;
  productName?: string;
  brandName?: string;
  imageUrl?: string;
  reviewText: string;
  ratingValue?: number;
  buzzScore?: number;
  upOrDown?: string;
  location?: string;
  isFirstReview?: boolean;
  createdAt: string;
  updatedAt: string;
}

function formatTimeAgo(dateStr: string): string {
  const now = Date.now();
  const then = new Date(dateStr).getTime();
  const diffMs = now - then;
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return 'just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHours = Math.floor(diffMin / 60);
  if (diffHours < 24) return `${diffHours}h ago`;
  const diffDays = Math.floor(diffHours / 24);
  if (diffDays < 7) return `${diffDays}d ago`;
  if (diffDays < 30) return `${Math.floor(diffDays / 7)}w ago`;
  return new Date(dateStr).toLocaleDateString();
}

export function StashPage() {
  const { user, token, logout, authFetch } = useAuth();
  const [reviews, setReviews] = useState<UserReview[]>([]);
  const [loading, setLoading] = useState(true);
  const [showReferralModal, setShowReferralModal] = useState(false);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const reviewsRef = useRef<HTMLDivElement>(null);

  // Check if user has referral data on their profile
  useEffect(() => {
    if (!token) return;
    if (localStorage.getItem('ws_referral_completed')) return;

    authFetch(`${API_BASE}/v1/profile/referral`)
      .then((res) => {
        if (res.status === 404 || res.status === 204) {
          // No profile/referral data — show modal
          setShowReferralModal(true);
        }
        return res.ok ? res.json() : null;
      })
      .then((json) => {
        if (json?.data && !json.data.referredBy && !json.data.discoverySource) {
          setShowReferralModal(true);
        }
      })
      .catch(() => {
        // If endpoint doesn't exist yet, show modal
        setShowReferralModal(true);
      });
  }, [token, authFetch]);

  useEffect(() => {
    if (!token) {
      setLoading(false);
      return;
    }

    async function fetchMyReviews() {
      try {
        const res = await authFetch(`${API_BASE}/v1/reviews/mine`);
        if (!res.ok) {
          setLoading(false);
          return;
        }
        const json = await res.json();
        const rawReviews = json.data || [];

        const withDetails = await Promise.all(
          rawReviews.map(async (review: UserReview) => {
            try {
              const prodRes = await fetch(`${API_BASE}/v1/products/${review.productId}`);
              if (prodRes.ok) {
                const prodJson = await prodRes.json();
                return {
                  ...review,
                  productName: prodJson.data?.productName || review.productId,
                  brandName: prodJson.data?.brandName,
                  imageUrl: prodJson.data?.imageUrl,
                };
              }
            } catch { /* ignore */ }
            return review;
          })
        );

        setReviews(withDetails);
      } catch {
        // Non-critical
      } finally {
        setLoading(false);
      }
    }

    fetchMyReviews();
  }, [token, authFetch]);

  const status = getCollectionStatus(reviews.length);
  const firstReviewCount = reviews.filter((r) => r.isFirstReview).length;

  const scrollToReviews = () => {
    reviewsRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="stash-page">
      <SEO
        title="My Stash - Your THC Drink Collection"
        description="Track your THC beverage collection. Rate, review, and earn badges as you explore more drinks."
        canonical="/stash"
        noindex
      />
      <div className="stash-page__header">
        <h1 className="stash-page__title">My Stash</h1>
        {user && (
          <div className="stash-page__user-info">
            {user.pictureUrl && (
              <img
                src={user.pictureUrl}
                alt={user.displayName || 'Profile'}
                className="stash-page__avatar"
                referrerPolicy="no-referrer"
              />
            )}
            <p className="stash-page__display-name">
              {user.displayName}
              {status.tier !== 'none' && (
                <span className="stash-page__badge">{status.icon} {status.label}</span>
              )}
            </p>
            <p className="stash-page__email">{user.email}</p>
          </div>
        )}
        <button
          type="button"
          className="stash-page__logout"
          onClick={logout}
        >
          Sign Out
        </button>
      </div>

      {/* Collection Milestones */}
      <section className="stash-page__milestones">
        <h2 className="stash-page__section-title">Collection Status</h2>
        <p className="stash-page__milestone-desc">
          Rate and review drinks to level up your collection status.
          {status.nextTier && (
            <> {status.nextTier.remaining} more to reach {status.nextTier.icon} {status.nextTier.label}!</>
          )}
        </p>
        <div className="stash-page__milestone-track">
          {ALL_TIERS.map((tier) => {
            const achieved = reviews.length >= tier.min;
            return (
              <div
                key={tier.tier}
                className={`stash-page__milestone${achieved ? ' stash-page__milestone--achieved' : ''}`}
              >
                <span className="stash-page__milestone-icon">{tier.icon}</span>
                <span className="stash-page__milestone-label">{tier.label}</span>
                <span className="stash-page__milestone-count">{tier.min} drinks</span>
              </div>
            );
          })}
        </div>
        <div className="stash-page__progress-bar">
          <div
            className="stash-page__progress-fill"
            style={{ width: `${Math.min((reviews.length / 50) * 100, 100)}%` }}
          />
        </div>
        <p className="stash-page__progress-text">
          {reviews.length} drink{reviews.length !== 1 ? 's' : ''} reviewed
        </p>
      </section>

      {/* First Reviews */}
      <section className="stash-page__first-reviews">
        <h2 className="stash-page__section-title">🥇 First Reviews</h2>
        {firstReviewCount > 0 ? (
          <div className="stash-page__first-reviews-achieved">
            <span className="stash-page__first-reviews-count">{firstReviewCount}</span>
            <span className="stash-page__first-reviews-label">
              drink{firstReviewCount > 1 ? 's' : ''} you reviewed first! 🎉
            </span>
          </div>
        ) : (
          <div className="stash-page__first-reviews-promo">
            <span className="stash-page__first-reviews-icon">🏆</span>
            <strong className="stash-page__first-reviews-title">
              {reviews.length > 0 ? 'Ready for the next challenge?' : 'No first reviews yet!'}
            </strong>
            <p className="stash-page__first-reviews-desc">
              {reviews.length > 0
                ? `You've reviewed ${reviews.length} drink${reviews.length > 1 ? 's' : ''} — nice! Now find one nobody's reviewed yet and earn the 🥇 First Reviewer badge. Look for the 🏆 on product pages!`
                : 'Find drinks with no reviews and be the first to share your experience. Look for the 🏆 badge on product pages!'}
            </p>
            <Link to="/thc-drinks" className="stash-page__first-reviews-cta">
              Discover & Review →
            </Link>
          </div>
        )}
      </section>

      {/* Drink Gallery */}
      {!loading && reviews.length > 0 && (
        <section className="stash-page__gallery">
          <div className="stash-page__gallery-grid">
            {reviews.map((review) => (
              <button
                key={review.productId}
                type="button"
                className="stash-page__gallery-item"
                onClick={scrollToReviews}
                aria-label={`${review.productName || 'Drink'} — scroll to review`}
              >
                {review.imageUrl ? (
                  <img
                    src={review.imageUrl}
                    alt={review.productName || 'Drink'}
                    className="stash-page__gallery-img"
                  />
                ) : (
                  <div className="stash-page__gallery-placeholder">
                    <span>🥤</span>
                  </div>
                )}
              </button>
            ))}
          </div>
        </section>
      )}

      {/* Reviews & Ratings — inline style like product page */}
      <section className="stash-page__section" ref={reviewsRef}>
        <h2 className="stash-page__section-title">Reviews & Ratings</h2>

        {loading ? (
          <p className="stash-page__loading">Loading...</p>
        ) : reviews.length > 0 ? (
          <div className="stash-page__review-list">
            {reviews.map((review) => (
              <article key={review.productId} className="stash-page__review-item">
                <div className="stash-page__review-header">
                  {user?.pictureUrl ? (
                    <img
                      src={user.pictureUrl}
                      alt={user.displayName || ''}
                      className="stash-page__review-avatar"
                      referrerPolicy="no-referrer"
                    />
                  ) : review.imageUrl ? (
                    <Link to={productUrl(review.productId, review.brandName, review.productName)}>
                      <img
                        src={review.imageUrl}
                        alt={review.productName || ''}
                        className="stash-page__review-thumb"
                      />
                    </Link>
                  ) : null}
                  <div className="stash-page__review-meta">
                    <Link
                      to={productUrl(review.productId, review.brandName, review.productName)}
                      className="stash-page__review-product"
                    >
                      {review.productName || review.productId}
                    </Link>
                    {review.brandName && (
                      <span className="stash-page__review-brand">{review.brandName}</span>
                    )}
                    <div className="stash-page__review-scores">
                      {review.ratingValue != null && review.ratingValue > 0 && (
                        <span className="stash-page__score-chip">★ {review.ratingValue.toFixed(1)}</span>
                      )}
                      {review.buzzScore != null && review.buzzScore > 0 && (
                        <span className="stash-page__score-chip stash-page__score-chip--buzz">⚡ {review.buzzScore}</span>
                      )}
                      {review.upOrDown && review.upOrDown !== 'Neutral' && (
                        <span className={`stash-page__score-chip stash-page__score-chip--${review.upOrDown.toLowerCase()}`}>
                          {review.upOrDown === 'Up' ? '↑ Up' : '↓ Down'}
                        </span>
                      )}
                    </div>
                    <time className="stash-page__review-time">
                      {formatTimeAgo(review.updatedAt || review.createdAt)}
                    </time>
                  </div>
                </div>
                {review.reviewText && (
                  <p className="stash-page__review-text">{review.reviewText}</p>
                )}
              </article>
            ))}
          </div>
        ) : (
          <p className="stash-page__empty">
            You haven't rated or reviewed any drinks yet. <Link to="/thc-drinks">Explore drinks</Link> to get started.
          </p>
        )}
      </section>

      {showReferralModal && (
        <ReferralModal token={token} onComplete={() => setShowReferralModal(false)} />
      )}

      {/* Feedback section */}
      <section className="stash-page__feedback">
        <p className="stash-page__feedback-text">Have thoughts on how we can improve?</p>
        <button
          type="button"
          className="stash-page__feedback-btn"
          onClick={() => setShowFeedbackModal(true)}
        >
          Give Us Feedback
        </button>
      </section>

      {showFeedbackModal && token && (
        <FeedbackModal accessToken={token} userEmail={user?.email} onClose={() => setShowFeedbackModal(false)} />
      )}

      {/* Account Deletion */}
      <section className="stash-page__danger-zone">
        {!showDeleteConfirm ? (
          <button
            type="button"
            className="stash-page__delete-btn"
            onClick={() => setShowDeleteConfirm(true)}
          >
            Delete My Account
          </button>
        ) : (
          <div className="stash-page__delete-confirm">
            <p className="stash-page__delete-warning">
              This will permanently delete your account, all reviews, ratings, and personal data.
              This cannot be undone.
            </p>
            <div className="stash-page__delete-actions">
              <button
                type="button"
                className="stash-page__delete-cancel"
                onClick={() => setShowDeleteConfirm(false)}
                disabled={deleting}
              >
                Cancel
              </button>
              <button
                type="button"
                className="stash-page__delete-confirm-btn"
                disabled={deleting}
                onClick={async () => {
                  setDeleting(true);
                  try {
                    const res = await authFetch(`${API_BASE}/v1/account`, { method: 'DELETE' });
                    if (res.ok) {
                      logout();
                    } else {
                      alert('Failed to delete account. Please try again.');
                    }
                  } catch {
                    alert('Failed to delete account. Please try again.');
                  } finally {
                    setDeleting(false);
                  }
                }}
              >
                {deleting ? 'Deleting...' : 'Yes, Delete Everything'}
              </button>
            </div>
          </div>
        )}
        <p className="stash-page__delete-note">
          Permanently removes your account, reviews, and all personal data.
        </p>
      </section>
    </div>
  );
}
